/** One row per category per year from sector GHG inventory builders. */
export type GhgInventoryEmissionRow = { year: number; category: string; emissions: number };

export type SectorInventoryHeadlines = {
  totalEmissions2005: number | undefined;
  totalEmissions2023: number | undefined;
  /** (2023 total − 2005 total) / 2005 total × 100 when both years exist and 2005 total is positive. */
  percentChange2023Vs2005: number | undefined;
  largestSectorSharePercent2023: number | undefined;
  largestSectorName2023: string | undefined;
};

export function computeSectorInventoryHeadlines(
  allEmissions: GhgInventoryEmissionRow[]
): SectorInventoryHeadlines {
  const hasYear = (y: number) => allEmissions.some((d) => d.year === y);
  const sumYear = (y: number) =>
    allEmissions.filter((d) => d.year === y).reduce((s, d) => s + (Number(d.emissions) || 0), 0);

  const t2005 = hasYear(2005) ? sumYear(2005) : undefined;
  const t2023 = hasYear(2023) ? sumYear(2023) : undefined;

  const percentChange2023Vs2005 =
    t2005 != null && t2005 > 0 && t2023 != null ? ((t2023 - t2005) / t2005) * 100 : undefined;

  const byCat = new Map<string, number>();
  for (const r of allEmissions) {
    if (r.year !== 2023) continue;
    const v = Number(r.emissions) || 0;
    byCat.set(r.category, (byCat.get(r.category) ?? 0) + v);
  }
  let largestName: string | undefined;
  let largestVal = 0;
  for (const [cat, em] of Array.from(byCat.entries())) {
    if (em > largestVal) {
      largestVal = em;
      largestName = cat;
    }
  }
  const total2023 = t2023 ?? 0;
  const largestSectorSharePercent2023 =
    total2023 > 0 && largestName != null ? (largestVal / total2023) * 100 : undefined;

  return {
    totalEmissions2005: t2005,
    totalEmissions2023: t2023,
    percentChange2023Vs2005,
    largestSectorSharePercent2023,
    largestSectorName2023: largestName,
  };
}
